import { useCallback, useEffect, useState } from 'react';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { DialogTitle, DialogDescription } from '@radix-ui/react-dialog';
import { DialogContent } from '@/components/ui/dialog';
import { ModalLayout, ModalTitle } from '@/components/ui/modal-layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldLabel, FieldContent, FieldError } from '@/components/ui/field';
import { Form } from '@components/Form';
import { useFormContext } from '@components/Form/FormProvider';
import { DropzoneUpload } from '@components/common/ImageUpload';
import { useTranslation } from '@hooks/useTranslation';
import { useCreateResaleItem } from '@hooks/resaleItems';
import { toast } from '@/hooks/use-toast';
import { ToastLinkDescription } from '@/components/ui/toast';
import { getFieldErrors } from '@api/client';
import { createResaleItemFormSchema, type CreateResaleItemFormData } from '@/react/schemas/resale-item.schema';
import { cn } from '@/lib/utils';

import { FIELD_SURFACE } from "@/components/ui/field-surface";
const INPUT_CLASSES =
  cn(FIELD_SURFACE, 'h-auto px-6 py-4 font-inter text-sm leading-normal placeholder:text-grey-light placeholder:font-normal');
const TEXTAREA_CLASSES =
  cn(FIELD_SURFACE, 'h-28 resize-none px-6 py-4 font-inter text-sm leading-normal placeholder:text-grey-light placeholder:font-normal');

interface CreateResaleItemFormProps {
  onClose: () => void;
}

const DEFAULT_VALUES = {
  name: '',
  vendor: '',
  max_quantity: null,
  recommended_retail_price: null,
  cost_to_company: null,
  item_notes: null,
  delivery_details: null,
} as unknown as CreateResaleItemFormData;

interface FormFieldsProps {
  images: File[];
  onImagesChange: (files: File[]) => void;
  error: Error | null;
}

function useServerErrors(error: Error | null) {
  const { form } = useFormContext();

  useEffect(() => {
    if (!error) return;
    const errors = getFieldErrors(error);
    if (errors?.name) {
      form.setFieldMeta('name', (prev) => ({
        ...prev,
        errorMap: { ...prev.errorMap, onSubmit: String(errors.name) },
      }));
    }
  }, [error, form]);
}

function FormFields({ images, onImagesChange, error }: FormFieldsProps) {
  const { form } = useFormContext();
  const { t } = useTranslation();
  const optionalLabel = t('create_modal.optional');
  useServerErrors(error);

  return (
    <div className="flex flex-col gap-8">
      <form.Field name="name">
        {(field) => {
          const fieldError = field.state.meta.errorMap.onSubmit || null;
          return (
            <Field>
              <FieldLabel className="font-inter text-sm font-semibold text-grey">
                {t('create_modal.name')}
              </FieldLabel>
              <FieldContent>
                <Input
                  value={(field.state.value as string) ?? ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('create_modal.name_placeholder')}
                  className={cn(INPUT_CLASSES, fieldError && 'border-chilli-red')}
                />
                {fieldError && <FieldError>{String(fieldError)}</FieldError>}
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      <div className="grid grid-cols-2 gap-8">
        <form.Field name="vendor">
          {(field) => (
            <Field>
              <FieldLabel className="font-inter text-sm font-semibold text-grey">
                {t('create_modal.vendor')}
              </FieldLabel>
              <FieldContent>
                <Input
                  value={(field.state.value as string) ?? ''}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('create_modal.vendor_placeholder')}
                  className={INPUT_CLASSES}
                />
              </FieldContent>
            </Field>
          )}
        </form.Field>

        <form.Field name="max_quantity">
          {(field) => (
            <Field>
              <FieldLabel className="font-inter text-sm font-semibold text-grey">
                {t('create_modal.daily_limit')}
              </FieldLabel>
              <FieldContent>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={(field.state.value as number | string | null) ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    field.handleChange(val === '' ? null : parseInt(val, 10));
                  }}
                  onBlur={field.handleBlur}
                  placeholder={t('create_modal.daily_limit_placeholder')}
                  className={INPUT_CLASSES}
                />
              </FieldContent>
            </Field>
          )}
        </form.Field>
      </div>

      <div className="grid grid-cols-2 gap-8">
        <form.Field name="recommended_retail_price">
          {(field) => (
            <Field>
              <FieldLabel className="font-inter text-sm font-semibold text-grey">
                {t('create_modal.retail_price')}
              </FieldLabel>
              <FieldContent>
                <div className="relative">
                  <span className="absolute left-6 top-1/2 -translate-y-1/2 font-inter text-sm text-grey">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={(field.state.value as number | string | null) ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.handleChange(val === '' ? null : parseFloat(val));
                    }}
                    onBlur={field.handleBlur}
                    className={cn(INPUT_CLASSES, 'pl-10')}
                  />
                </div>
              </FieldContent>
            </Field>
          )}
        </form.Field>

        <form.Field name="cost_to_company">
          {(field) => (
            <Field>
              <FieldLabel className="font-inter text-sm font-semibold text-grey">
                {t('create_modal.cost_price')}
              </FieldLabel>
              <FieldContent>
                <div className="relative">
                  <span className="absolute left-6 top-1/2 -translate-y-1/2 font-inter text-sm text-grey">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={(field.state.value as number | string | null) ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      field.handleChange(val === '' ? null : parseFloat(val));
                    }}
                    onBlur={field.handleBlur}
                    className={cn(INPUT_CLASSES, 'pl-10')}
                  />
                </div>
              </FieldContent>
            </Field>
          )}
        </form.Field>
      </div>

      <div className="grid grid-cols-2 gap-8">
        <form.Field name="item_notes">
          {(field) => (
            <Field>
              <div className="flex items-center justify-between">
                <FieldLabel className="font-inter text-sm font-semibold text-grey">
                  {t('create_modal.item_notes')}
                </FieldLabel>
                <span className="font-inter text-xs text-grey">{optionalLabel}</span>
              </div>
              <FieldContent>
                <Textarea
                  value={(field.state.value as string | null) ?? ''}
                  onChange={(e) => field.handleChange(e.target.value === '' ? null : e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('create_modal.item_notes_placeholder')}
                  className={TEXTAREA_CLASSES}
                />
              </FieldContent>
            </Field>
          )}
        </form.Field>

        <form.Field name="delivery_details">
          {(field) => (
            <Field>
              <div className="flex items-center justify-between">
                <FieldLabel className="font-inter text-sm font-semibold text-grey">
                  {t('create_modal.delivery_details')}
                </FieldLabel>
                <span className="font-inter text-xs text-grey">{optionalLabel}</span>
              </div>
              <FieldContent>
                <Textarea
                  value={(field.state.value as string | null) ?? ''}
                  onChange={(e) => field.handleChange(e.target.value === '' ? null : e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder={t('create_modal.delivery_details_placeholder')}
                  className={TEXTAREA_CLASSES}
                />
              </FieldContent>
            </Field>
          )}
        </form.Field>
      </div>

      <Field>
        <FieldLabel className="font-inter text-sm font-semibold text-grey">
          {t('create_modal.upload_images')}
        </FieldLabel>
        <DropzoneUpload files={images} onFilesChange={onImagesChange} />
      </Field>
    </div>
  );
}

function FormFooter() {
  const { form, isPending } = useFormContext();
  const { t } = useTranslation();

  return (
    <form.Subscribe selector={(state) => ({ canSubmit: state.canSubmit, isSubmitting: state.isSubmitting })}>
      {({ canSubmit, isSubmitting }) => {
        const isDisabled = !canSubmit || isSubmitting || Boolean(isPending);
        return (
          <div className="flex w-full justify-end">
            <Button
              type="submit"
              disabled={isDisabled}
            >
              {t('create_modal.create')}
            </Button>
          </div>
        );
      }}
    </form.Subscribe>
  );
}

export function CreateResaleItemForm({ onClose }: CreateResaleItemFormProps) {
  const { t } = useTranslation();
  const [images, setImages] = useState<File[]>([]);
  const createResaleItem = useCreateResaleItem();

  const handleSubmit = useCallback(async (values: CreateResaleItemFormData) => {
    let resaleItem;
    try {
      resaleItem = (await createResaleItem.mutateAsync({
        name: values.name,
        supplier: values.vendor || null,
        max_quantity: values.max_quantity,
        recommended_retail_price: values.recommended_retail_price,
        cost_to_company: values.cost_to_company,
        notes: values.item_notes,
        delivery_information: values.delivery_details,
        images: images.length > 0 ? images : undefined,
      })).resale_item;
    } catch {
      // Error surfaced via createResaleItem.error → useServerErrors
      return;
    }
    onClose();
    const { dismiss } = toast({
      description: (
        <ToastLinkDescription
          name={resaleItem.name}
          href={`/resale_items/${resaleItem.id}/details`}
          label={t('create_modal.created')}
          onNavigate={() => dismiss()}
        />
      ),
    });
  }, [createResaleItem, images, onClose, t]);

  const title = t('create_modal.title');

  return (
    <DialogContent
      aria-describedby={undefined}
      onOpenAutoFocus={(e) => e.preventDefault()}
      onEscapeKeyDown={onClose}
      className="max-w-dialog max-h-[90vh] rounded-input border-dinnerplate-white bg-body p-0 outline-none overflow-hidden [&>button:last-child]:hidden"
    >
      <VisuallyHidden.Root>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{title}</DialogDescription>
      </VisuallyHidden.Root>
      <Form
        schema={createResaleItemFormSchema}
        defaultValues={DEFAULT_VALUES}
        onSubmit={handleSubmit}
        isPending={createResaleItem.isPending}
        className="flex flex-1 flex-col min-h-0"
      >
        <ModalLayout
          as="div"
          title={<ModalTitle>{title}</ModalTitle>}
          onClose={onClose}
          footer={<FormFooter />}
        >
          <FormFields images={images} onImagesChange={setImages} error={createResaleItem.error} />
        </ModalLayout>
      </Form>
    </DialogContent>
  );
}
